/**
 * Searchable multi-select agent picker.
 *
 * Mirrors `ModelPicker` — type-to-filter, wider popper so full names and
 * specializations are readable, rich option rendering (avatar · name ·
 * specialization chip · version), a Select-all / Clear helper-text action.
 *
 * Value shape is a flat list of composite keys in the form `{spec}|{version}`
 * so the parent can round-trip to the backend's `AgentIdInput[]` without
 * needing to map Agent objects around. The parent supplies `agents` (the
 * catalogue) and a `value: string[]` of keys.
 */
import React from 'react';
import {Autocomplete, Avatar, Box, Chip, TextField, Typography} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import type {Agent} from '../../types/generated/graphql';
import {pcolors} from '../../theme/planapusTheme';
import {
  humanizeSpecialization,
  shortSpecialization,
  resolveAgentThumbnailUrl,
  colorForString,
} from './agentLabels';

export function agentKey(a: {specialization: string; version: string}): string {
  return `${a.specialization}|${a.version}`;
}

function AgentOption({agent}: {agent: Agent}) {
  const src = resolveAgentThumbnailUrl(agent.thumbnails || []);
  return (
    <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 1.5, py: 0.25, minWidth: 520}}>
      <Avatar
        src={src ?? undefined}
        sx={{
          width: 28,
          height: 28,
          flex: 'none',
          fontSize: 11,
          fontWeight: 600,
          bgcolor: colorForString(String(agent.specialization)),
        }}
      >
        {src ? null : <SmartToyIcon sx={{fontSize: 14}} />}
      </Avatar>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 13, color: pcolors.textPrimary, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
          {agent.name}
        </Typography>
        <Typography sx={{fontSize: 11, color: pcolors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
          {humanizeSpecialization(agent.specialization)}
        </Typography>
      </Box>
      <Typography sx={{fontSize: 11, color: pcolors.textSecondary, fontFamily: 'monospace', flex: 'none'}}>
        v{agent.version}
      </Typography>
    </Box>
  );
}

export interface AgentPickerProps {
  agents: Agent[];
  value: string[];
  onChange: (keys: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

export function AgentPicker(props: AgentPickerProps) {
  const byKey = new Map(props.agents.map(a => [agentKey(a), a]));
  const selectedAgents = props.value
    .map(k => byKey.get(k))
    .filter((a): a is Agent => !!a);

  const allSelected =
    props.agents.length > 0 && selectedAgents.length === props.agents.length;

  const selectAll = () => props.onChange(props.agents.map(agentKey));
  const clearAll = () => props.onChange([]);

  // Give the popper room so full agent names + humanized specializations don't
  // truncate. Mirrors ModelPicker.
  const popperSlotProps = {
    popper: {
      style: {minWidth: 560},
      placement: 'bottom-start' as const,
    },
    paper: {sx: {'& .MuiAutocomplete-listbox': {maxHeight: 420, fontSize: 12}}},
  };

  return (
    <Autocomplete
      multiple
      size={props.size || 'small'}
      fullWidth={props.fullWidth ?? true}
      disabled={props.disabled}
      options={props.agents}
      value={selectedAgents}
      disableCloseOnSelect
      getOptionLabel={a => a.name}
      isOptionEqualToValue={(a, b) => agentKey(a) === agentKey(b)}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (!q) return opts;
        return opts.filter(a => {
          const spec = humanizeSpecialization(a.specialization).toLowerCase();
          const raw = String(a.specialization).toLowerCase();
          return (
            a.name.toLowerCase().includes(q) ||
            spec.includes(q) ||
            raw.includes(q) ||
            a.version.toLowerCase().includes(q)
          );
        });
      }}
      renderTags={(val, getTagProps) =>
        val.map((a, i) => (
          <Chip
            size="small"
            {...getTagProps({index: i})}
            label={shortSpecialization(a.specialization)}
            sx={{fontSize: 11, fontFamily: 'monospace', maxWidth: 180}}
            key={agentKey(a)}
          />
        ))
      }
      renderOption={(optProps, agent) => (
        <li {...optProps} key={agentKey(agent)} style={{...optProps.style, paddingLeft: 8, paddingRight: 8}}>
          <AgentOption agent={agent} />
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={props.label || `Agents (${selectedAgents.length}/${props.agents.length})`}
          placeholder={props.placeholder || 'Search by name, specialization, or version…'}
          helperText={
            <Box component="span" sx={{display: 'inline-flex', gap: 1, alignItems: 'center', flexWrap: 'wrap'}}>
              <Box
                component="span"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  allSelected ? clearAll() : selectAll();
                }}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: pcolors.blue[700],
                  '&:hover': {color: pcolors.blue[900]},
                }}
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </Box>
              {selectedAgents.length > 0 && !allSelected && (
                <Box
                  component="span"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearAll();
                  }}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    color: pcolors.textSecondary,
                    '&:hover': {color: pcolors.textPrimary},
                  }}
                >
                  Clear
                </Box>
              )}
              <Box component="span" sx={{color: pcolors.textMuted}}>
                {selectedAgents.length === 0
                  ? '(none selected = run all)'
                  : `${selectedAgents.length} of ${props.agents.length} selected`}
              </Box>
            </Box>
          }
        />
      )}
      onChange={(_, val) => props.onChange(val.map(agentKey))}
      slotProps={popperSlotProps}
    />
  );
}
